import { useAssetVizInternal } from '@asset-viz/AssetVizInternalProvider'
import { AssetVizMode } from '@asset-viz/defines'
import React from 'react'
import styled from 'styled-components'
import { GeckoMarkupLabel, GeckoMarkupLayer } from 'types'

import { PrimitiveHtmlOverlay3D } from './util/PrimitiveHtmlOverlay3D'
import { useMarkupLayerProperties } from './util/useMarkupLayerProperties'

export const LabelContainer = styled.div<{
  $backgroundColor: string
  $screenshotMode?: boolean
}>`
  word-wrap: break-word;
  white-space: break-spaces;

  width: 100%;
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;

  border-radius: 0.25rem;
  padding: ${({ $screenshotMode }) =>
    $screenshotMode ? '0.1rem 0.1rem' : '0.25rem 0.5rem'};

  background-color: ${({ $backgroundColor }) => $backgroundColor};
  opacity: ${({ $screenshotMode }) => ($screenshotMode ? '0.8' : '1')};
`

export const Label = styled.small<{
  $textColor: string
  $screenshotMode?: boolean
}>`
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;

  word-wrap: break-word;
  white-space: break-spaces;

  user-select: none;
  cursor: default;
  color: ${({ $textColor }) => $textColor};
  opacity: unset;
  font-size: ${({ $screenshotMode }) => ($screenshotMode ? '24px' : '12px')};

  &:hover {
    -webkit-line-clamp: 8 !important;
  }
`

/**
 * Renders a label markup primitive on the surface of a component, mapping via
 * its surface's UVs
 * @param markupLayer       The layer this primitive belongs to
 * @param primitive         The primitive being rendered
 * @constructor
 */
export const LabelPrimitive3D: React.FC<{
  markupLayer: GeckoMarkupLayer
  primitive: GeckoMarkupLabel
}> = ({ markupLayer, primitive }) => {
  const { labelBackgroundColorString, featureLabelColorString, mode } =
    useAssetVizInternal()
  const isScreenshotMode = mode === AssetVizMode.Screenshot

  const { onWrite, refreshAndSelectLayer, deletePrimitive } =
    useAssetVizInternal()
  const { isSelected } = useMarkupLayerProperties({
    markupLayer,
    primitive,
  })

  //----------------------------------------------------------------------------

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (e.currentTarget.value.length === 0)
        deletePrimitive(primitive, markupLayer)
      else commitLabel(e.currentTarget.value)
    } else if (e.key === 'Escape') {
      e.nativeEvent.stopImmediatePropagation()
      refreshAndSelectLayer(markupLayer)
    } else if (
      (e.key === 'Backspace' || e.key === 'Delete') &&
      e.currentTarget.value.length === 0
    ) {
      deletePrimitive(primitive, markupLayer)
    }
  }

  const commitLabel = (text: string) => {
    onWrite(
      GeckoMarkupLabel.CreateFromJSON(
        primitive.id,
        markupLayer.id,
        {
          kind: 'label',
          x: primitive.coords.x.num,
          y: primitive.coords.y.num,
          text,
        },
        markupLayer.data.geometry
      )
    ).then(() => refreshAndSelectLayer(markupLayer))
  }

  return (
    <PrimitiveHtmlOverlay3D
      markupLayer={markupLayer}
      primitive={primitive}
      coords={primitive.coords}
      anchor={primitive.anchor}>
      <LabelContainer
        $backgroundColor={labelBackgroundColorString}
        $screenshotMode={isScreenshotMode}>
        {isSelected ? (
          <input
            autoFocus={true}
            onKeyDown={(e) => onKeyDown(e)}
            placeholder={'Enter text'}
            defaultValue={primitive.text}
            style={{ fontSize: '12px' }}
          />
        ) : (
          <Label
            $textColor={featureLabelColorString}
            $screenshotMode={isScreenshotMode}>
            {primitive.text}
          </Label>
        )}
      </LabelContainer>
    </PrimitiveHtmlOverlay3D>
  )
}
